import React, { useState } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES } from "../../utils/constants";
import { getScenarioNumByPath, getTimer } from "../../utils/helpers";
import { updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import {
  modalConfirm,
  modalQuestionResolution,
  modalHint,
  modalHintShow
} from "../../utils/logger";
import { IconWriting } from "@tabler/icons-react";
import CheckButton from "../../components/CheckButton";
import estasseguro from "../../assets/bg/estasseguro-avatar-blue.png";
import respuestacorrecta from "../../assets/bg/correcto-avatar.png";
import respuestaincorrecta from "../../assets/bg/incorrecto-avatar.png";
import { useVideoWithSound } from "../../hooks/useVideoWithSound";
import btnPistaBlue from "../../assets/btnPistaBlue.png";
import imageHint from "../../assets/hints/imageHintBlue.png";
import imageHintUsed from "../../assets/btnPistaBlue.png";
import imgPista from "../../assets/hints/hint-eventos-adversos-2.png"
import unIntento from "../../assets/un-intento-blue.png"
import dosIntentos from "../../assets/dos-intentos-blue.png"

function Scenario4SuspensionTratamiento({ intl }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const group = useSelector((state) => state.group);

  const activeScenario = getScenarioNumByPath(location.pathname);

  const { videoRef, canPlayWithSound } = useVideoWithSound({
    autoPlay: true,
    volume: 0.8
  });

  const QUIZ_ID = QUIZZES.SUSPENSION_DE_TRATAMIENTO;
  const SCENARIO_ID = SCENARIOS.SCENARIO_4;

  const [inputValue, setInputValue] = useState("");
  const [isCompleted, setIsCompleted] = useState(false);
  const [hintUsed, setHintUsed] = useState(false);
  const [hintPenalty, setHintPenalty] = useState(0);
  const [hintAppliedToScore, setHintAppliedToScore] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 2 intentos con envío único (suma final)
  const [attempt, setAttempt] = useState(1);            // 1..2
  const [pendingScore, setPendingScore] = useState(0);  // acumulado a enviar al final

  const NUMBERATTEMPTS = 2;
  const CORRECT_ANSWER = 8;
  const CORRECT_ANSWER_POINTS = 10;
  const WRONG_ANSWER_POINTS = -5;
  const CORRECT_ANSWER_POINTS_2 = 3;
  const WRONG_ANSWER_POINTS_2 = 0;

  async function submitAnswer(finalScore, correctFinal) {
    const totalTime = getTimer();
    dispatch(updateTime(totalTime, 1));

    try {
      const res = await postAnswer({
        scenario: SCENARIO_ID,
        quiz: QUIZ_ID,
        group: group?._id,
        score: finalScore, // único envío con suma final
        time: totalTime,
        lastVisited: ROUTES.SCENARIO4_RETROALIMENTACION,
        correct: correctFinal
      });

      if (res.status === 201) {
        setIsCompleted(true);
        navigate(ROUTES.SCENARIO4_RETROALIMENTACION);
      }
    } catch (error) {
      const status = error?.response?.status;
      const code = error?.response?.data?.code;

      if (status === 409 && code === "E_DUPLICATE_ANSWER") {
        await modalQuestionResolution({
          correct: false,
          question: "Respuesta ya registrada",
          textHtml: `<div class="txt32 txtBlue bold">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, pueden continuar con el resto del juego.</div>`,
          customConfirmButtonHtml: "Ir a la conclusión",
        });
        navigate(ROUTES.SCENARIO4_RETROALIMENTACION);
        return;
      }

      await modalQuestionResolution({
        correct: false,
        question: "No se pudo guardar",
        textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
        customConfirmButtonHtml: "Aceptar",
      });

      setIsSubmitting(false);
    }
  }

  const useHint = async () => {
    const hint = `<img src=${imgPista} alt="Pista" style="width:100%;" />`;
  
    if (hintUsed) {
      await modalHintShow({ hint, imageHintUsed, color: "blue", big: true });
      return;
    }
    const { used } = await modalHint({ image: imageHint, imageHintUsed, hint, color: "blue", big: true });
    if (used && !hintUsed) {
      setHintUsed(true);
      setHintPenalty(3); // se aplicará 1 sola vez en el primer intento que compute puntos (fallo o acierto)
    }
  };

  function checkAnswer() {
    const trimmed = (inputValue ?? "").toString().trim();
    if (trimmed === "") {
      modalQuestionResolution({
        correct: false,
        textHtml: `<img src=${estasseguro} alt="Debes ingresar un valor" class="estasseguro" />
                   <div class="text-center txtTitulos mt-3 blue">Debes ingresar un valor</div>`,
      });
      return;
    }

    const normalized = trimmed.replace(",", ".");
    const userValue = Number(normalized);
    if (Number.isNaN(userValue)) {
      modalQuestionResolution({
        correct: false,
        textHtml: `<img src=${estasseguro} alt="Valor inválido" class="estasseguro" />
                   <div class="text-center txtTitulos mt-3 blue">Introduce un número válido</div>`,
      });
      return;
    }

    const isIntegerOrZeroDecimal = Number.isInteger(userValue);
    const isCorrect = isIntegerOrZeroDecimal && userValue === CORRECT_ANSWER;

    modalConfirm({ estasseguro, color: "blue" }).then((res) => {
      if (!res.isConfirmed) return;

      setIsSubmitting(true);
      const hintThisAttempt = (hintUsed && !hintAppliedToScore) ? hintPenalty : 0;

      if (isCorrect) {
        // ✅ Correcto (1º o 2º intento)
        const base = (attempt === 1) ? CORRECT_ANSWER_POINTS : CORRECT_ANSWER_POINTS_2;
        const attemptDelta = base - hintThisAttempt;
        const newScore = pendingScore + attemptDelta;
        if (hintThisAttempt) setHintAppliedToScore(true);

        modalQuestionResolution({
          correct: true,
          question: "Porcentaje pacientes cáncer de próstata",
          textHtml: `<img src=${respuestacorrecta} alt="Respuesta correcta" style='max-width:420px' />
                     <div class="txt32 bold pt-4">Ganaron ${attemptDelta} puntos en este intento${hintThisAttempt ? " (-3 pista)" : ""}</div>
                     ${attempt === 2 ? `<div class="txt20 mt-2">Correcto en el segundo intento</div>` : ""}
                     <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>`,
          customConfirmButtonHtml: "Continuar",
          color: "blue"
        }).then(() => {
          submitAnswer(newScore, true); // envío único final
        });

        return;
      }

      // ❌ Incorrecto
      if (attempt < NUMBERATTEMPTS) {
        // 1er fallo: resta -5 (y -3 si aplica pista), NO enviamos aún
        const attemptDelta = WRONG_ANSWER_POINTS - hintThisAttempt; // -5 o -8 si pista aquí
        const newScore = pendingScore + attemptDelta;
        if (hintThisAttempt) setHintAppliedToScore(true);

        modalQuestionResolution({
          correct: false,
          question: "Porcentaje pacientes cáncer de próstata",
          textHtml: `<img src=${respuestaincorrecta} alt="Respuesta incorrecta" style='max-width:420px' />
                     <div class="txt28 bold pt-3">Respuesta incorrecta ${attemptDelta} puntos${hintThisAttempt ? " (incluye -3 por pista)" : ""}</div>
                     <div class="txt22 mt-2">Les queda <b>1</b> intento. Ajusta el valor e inténtalo de nuevo.</div>
                     <div class="txt20 mt-2">Acumulado hasta ahora: <b>${newScore}</b></div>`,
          customConfirmButtonHtml: "Intentar de nuevo",
          color: "blue"
        }).then(() => {
          setPendingScore(newScore);
          setAttempt(attempt + 1);
          // Si prefieres limpiar el input:
          // setInputValue("");
          setIsSubmitting(false);
        });

        return;
      }

      // 2º fallo: delta 0 (o -3 si justo aquí aplica pista por 1ª vez), ahora sí enviamos
      const attemptDelta = WRONG_ANSWER_POINTS_2 - hintThisAttempt; // 0 o -3
      const newScore = pendingScore + attemptDelta;
      if (hintThisAttempt) setHintAppliedToScore(true);

      modalQuestionResolution({
        correct: false,
        question: "Porcentaje pacientes cáncer de próstata",
        textHtml: `<img src=${respuestaincorrecta} alt="Respuesta incorrecta" style='max-width:420px' />
                   <div class="txt32 bold pt-4">Han agotado los intentos</div>
                   ${hintThisAttempt ? `<div class="txt20 mt-2">Se aplicó -3 por pista en este intento</div>` : ""}
                   <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>`,
        customConfirmButtonHtml: "Continuar",
        color: "blue"
      }).then(() => {
        submitAnswer(newScore, false); // envío único final
      });
    });
  }

  const remainingAttempts = NUMBERATTEMPTS - attempt + 1;

  return (
    <div className="h-100">
      <div className="container-fluid position-relative h-100 bgCircleBlue2">
        <div className="row h-100 d-flex align-items-center">
          <div className="col-md-12 h-100 d-flex flex-column justify-content-center px-4">
            <p className="txtTexto2 recuadro recuadro-blue py-4" style={{backgroundColor: "#FFFFFFAA"}}>
              En el estudio de Chi <i>et al.</i> (de 2019) Apalutamide for Metastatic, Castration-Sensitive Prostate Cancer, sobre el tratamiento con apalutamida en pacientes con cáncer de próstata, algunos pacientes <strong>no pudieron continuar</strong> con el tratamiento debido a efectos adversos derivados de la <strong>toxicidad</strong>.
            </p>

            <h3 className="txtTexto2 blue bold position-relative mt-4">
              De acuerdo con la evidencia del artículo, completa la frase
            </h3>

            <div className="d-flex justify-content-between">
              <h5 className="mb-4 txt32 text-left my-2 blue">
                <IconWriting size={40} color="#69D0FF" stroke={1} /> Escribe el valor en números
              </h5>
              <div className="mb-3">
                <img src={remainingAttempts == 2 ? dosIntentos : unIntento} className="imgIntentos" />
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-start txtTexto2">
              <span className="me-2">El</span>
              <input
                type="number"
                inputMode="decimal"
                className="form-control text-center mx-2 recuadro-blue"
                style={{ width: "80px", fontSize: "30px", color: "#000 !important" }}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                min={0}
                max={100}
              />
              <span className="ms-2">% de pacientes suspendió<br/>el tratamiento por toxicidad.</span>
            </div>

            <div className="d-flex justify-content-between mt-5">
              <div className="position-relative d-inline-block">
                <img
                  src={btnPistaBlue}
                  className={`btnPista mt-2 ${hintUsed ? "used" : ""}`}
                  onClick={useHint}
                  alt={hintUsed ? "Pista usada" : "Usar pista"}
                  title={hintUsed ? "Pista usada (-3)" : "Usar pista (-3 puntos)"}
                />
                {hintUsed && <span className="badge-penalty">-3</span>}
              </div>
              <CheckButton
                text="Comprobar respuesta"
                onClick={checkAnswer}
                disabled={isCompleted || isSubmitting}
                color="blue"
                loading={isSubmitting}
              />
            </div>
          </div>
        </div>
      </div>

      <ScenarioBar activeScenario={activeScenario} />
    </div>
  );
}

export default injectIntl(Scenario4SuspensionTratamiento);
